// Google Analytics (GA4). Does nothing at all unless the server hands the page a
// measurement ID through /config.js, which it does only when GA_MEASUREMENT_ID is set in
// deploy/.env. For local testing, VITE_GA_MEASUREMENT_ID works too.
//
// Nothing that identifies a person is sent: no email, name, username or account id. Page
// addresses go without their query string, and invite links lose their token, because
// those carry password-reset and invite secrets.

declare global {
  interface Window {
    JOOKABLE_GA_ID?: string | null;
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/;
let measurementId: string | null = null;

const configuredId = (): string | null => {
  const fromServer = typeof window !== 'undefined' ? window.JOOKABLE_GA_ID : null;
  const fromBuild = (import.meta as any).env ? (import.meta as any).env.VITE_GA_MEASUREMENT_ID : null;
  const id = String(fromServer || fromBuild || '').trim().toUpperCase();
  return MEASUREMENT_ID.test(id) ? id : null;
};

/** Loads gtag.js once, if analytics is switched on. Safe to call more than once. */
export const initAnalytics = () => {
  if (measurementId) {
    return;
  }
  const id = configuredId();
  if (!id) {
    return;
  }
  measurementId = id;

  window.dataLayer = window.dataLayer || [];
  // gtag.js reads the arguments object itself, so this must push `arguments`, not an array.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  // Consent Mode. In the EEA, the UK and Switzerland nothing is stored and Google gets only
  // cookieless pings until the visitor accepts on the banner (AnalyticsConsent.tsx).
  // Everywhere else it is on from the start. Google decides the region from the visitor's
  // IP address, so this holds even when the banner's own guess (needsConsentBanner) is off.
  window.gtag('consent', 'default', { ...consentState('denied'), region: CONSENT_REGIONS, wait_for_update: 500 });
  window.gtag('consent', 'default', consentState('granted'));
  const choice = storedConsent();
  if (choice) {
    window.gtag('consent', 'update', consentState(choice));
  }

  window.gtag('js', new Date());
  // Page views are sent by trackPageView on each route change, with a cleaned address.
  window.gtag('config', id, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);
};

export const analyticsEnabled = () => !!measurementId;

// The EU and EEA, the UK and Switzerland: where analytics cookies need consent first.
const CONSENT_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT',
  'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO',
  'GB', 'CH',
];

type Consent = 'granted' | 'denied';
const CONSENT_KEY = 'jookable.analyticsConsent';

// Analytics plus Google signals (the ad_ keys), which is where age, gender and interests
// come from. Jookable shows no ads.
const consentState = (value: Consent) => ({
  analytics_storage: value,
  ad_storage: value,
  ad_user_data: value,
  ad_personalization: value,
});

export const storedConsent = (): Consent | null => {
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch (error) {
    return null;
  }
};

/** The visitor's answer on the banner. Remembered in this browser. */
export const setConsent = (value: Consent) => {
  try {
    window.localStorage.setItem(CONSENT_KEY, value);
  } catch (error) {
    // Storage refused: the answer holds for this visit only.
  }
  if (measurementId) {
    window.gtag('consent', 'update', consentState(value));
  }
};

/**
 * Whether to ask. The browser's time zone stands in for the region, since the page has no
 * other way to know it: Europe and its Atlantic islands. That errs towards asking (Moscow
 * and Istanbul get asked too), which is the safe side.
 */
export const needsConsentBanner = (timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '') =>
  !!measurementId &&
  !storedConsent() &&
  (/^Europe\//.test(timeZone) ||
    /^Atlantic\/(Azores|Canary|Faroe|Faeroe|Madeira|Reykjavik)$/.test(timeZone) ||
    timeZone === 'Arctic/Longyearbyen');

/**
 * The address as it is reported: no query string or hash, and invite tokens replaced.
 * Player codes stay, since they are what tells one venue's use from another's.
 */
export const cleanPath = (pathname: string) => {
  const path = (pathname || '/').replace(/^\/invite\/[^/]+/, '/invite/:token');
  return path || '/';
};

const PAGE_TITLES: Array<[RegExp, string]> = [
  [/^\/$/, 'Sign in'],
  [/^\/signup/, 'Sign up'],
  [/^\/recover-password-complete/, 'Reset password'],
  [/^\/recover-password/, 'Forgot password'],
  [/^\/home/, 'Home'],
  [/^\/player\//, 'TV player'],
  [/^\/jukebox\//, 'Jukebox'],
  [/^\/control\//, 'Control panel'],
  [/^\/admin/, 'Admin'],
  [/^\/invite\//, 'Admin invite'],
  [/^\/verify-email/, 'Verify email'],
  [/^\/terms/, 'Terms'],
  [/^\/privacy/, 'Privacy'],
  [/^\/venues/, 'Venues'],
];

/** A readable page name, so reports group every jukebox together whatever its code. */
export const pageTitle = (path: string) => {
  const match = PAGE_TITLES.find(([pattern]) => pattern.test(path));
  return match ? match[1] : 'Not found';
};

/** The player code in a /player, /jukebox or /control address, or undefined. */
export const playerCodeFromPath = (path: string) => {
  const match = /^\/(player|jukebox|control)\/([^/]+)/.exec(path);
  return match ? match[2].toLowerCase() : undefined;
};

export const trackPageView = (pathname: string) => {
  if (!measurementId) {
    return;
  }
  const path = cleanPath(pathname);
  const location = `${window.location.origin}${path}`;
  const title = pageTitle(path);
  // Set, not just sent with the page view, so later events on this page carry the cleaned
  // address too instead of the real one with its query string.
  window.gtag('set', { page_location: location, page_title: title });
  window.gtag('event', 'page_view', {
    page_location: location,
    page_path: path,
    page_title: title,
    player_code: playerCodeFromPath(path),
  });
};

/** Sends one event. Keep params free of anything that identifies a person. */
export const trackEvent = (name: string, params: Record<string, any> = {}) => {
  if (!measurementId) {
    return;
  }
  window.gtag('event', name, params);
};

/**
 * Who is using it, in broad strokes, for GA's user-scoped custom dimensions. Never the
 * account itself: only whether they are signed in, a Jookable+ member, an admin, signed in
 * with Google only, or using two-step sign-in.
 */
export const setUserProperties = (user: { id?: number; isPlus?: boolean; role?: string; hasPassword?: boolean; mfaEnabled?: boolean } | null) => {
  if (!measurementId) {
    return;
  }
  const signedIn = !!(user && user.id);
  window.gtag('set', 'user_properties', {
    signed_in: signedIn ? 'yes' : 'no',
    plus_member: signedIn ? (user.isPlus ? 'yes' : 'no') : null,
    account_role: signedIn ? user.role || 'normal' : null,
    google_only: signedIn ? (user.hasPassword === false ? 'yes' : 'no') : null,
    two_step: signedIn ? (user.mfaEnabled ? 'yes' : 'no') : null,
  });
};

// A web bump purchase starts here and finishes on Stripe's page, so which pack was chosen
// is kept for the moment the buyer comes back.
const PENDING_PURCHASE = 'jookable.pendingPurchase';

interface PackForAnalytics {
  productId: string;
  name: string;
  amount: number;
  currency: string;
}

const purchaseParams = (pack: PackForAnalytics, playerCode?: string) => ({
  currency: pack.currency.toUpperCase(),
  value: pack.amount / 100,
  player_code: playerCode,
  items: [{ item_id: pack.productId, item_name: pack.name, price: pack.amount / 100, quantity: 1 }],
});

export const trackCheckoutStarted = (pack: PackForAnalytics, playerCode?: string) => {
  if (!measurementId) {
    return;
  }
  try {
    window.sessionStorage.setItem(PENDING_PURCHASE, JSON.stringify({ pack, playerCode }));
  } catch (error) {
    // Private browsing can refuse storage. The purchase then just goes unrecorded here.
  }
  trackEvent('begin_checkout', purchaseParams(pack, playerCode));
};

/** Back from Stripe. Only the page's own ?bumps= flag says how it went. */
export const trackCheckoutReturned = (outcome: 'paid' | 'cancelled') => {
  let pending: { pack: PackForAnalytics; playerCode?: string } | null = null;
  try {
    pending = JSON.parse(window.sessionStorage.getItem(PENDING_PURCHASE) || 'null');
    window.sessionStorage.removeItem(PENDING_PURCHASE);
  } catch (error) {
    pending = null;
  }
  if (!measurementId) {
    return;
  }
  if (outcome === 'cancelled') {
    trackEvent('checkout_cancelled', pending ? purchaseParams(pending.pack, pending.playerCode) : {});
    return;
  }
  trackEvent('purchase', pending ? purchaseParams(pending.pack, pending.playerCode) : {});
};
